import { BrowserStorage } from '@estee/elc-universal-utils';

import { SocialSignUpType, SocialSignUpTypeKeys } from '~api/RampPgApiSdk';
import { StorageKey } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import {
    SOCIAL_QUERY_CONNECT,
    SOCIAL_QUERY_DISCONNECT,
    SocialCallbackQueryParams,
    SocialPageLoadType,
    STATE_DIVIDER
} from '~constants/SocialConstants';
import { ISocialProvider } from '~domain/entities/Config';

import { getRandom } from './GetRandom';

const formatData = (name: string, value?: string | null) => {
    return value ? `${name}:${value}${STATE_DIVIDER}` : '';
};

export const generateSocialState = (
    socialProvider: SignInType,
    store?: string,
    receiptId?: string
) => {
    const providerSource = BrowserStorage.getItem(
        StorageKey.SOCIAL_SIGN_UP_PROVIDER_SOURCE,
        'sessionStorage'
    );

    const data: { [key: string]: undefined | null | string | SignInType } = {
        provider: socialProvider,
        source: providerSource,
        store: store,
        receiptId: receiptId
    };

    const stateData = Object.keys(data).reduce((state: string, key: string) => {
        return state + formatData(key, data[key]);
    }, '');

    return `${stateData}${getRandom()}`;
};

const getStateData = (state: string, key: string) => {
    const regexPattern = new RegExp(`${key}:([^|]*)`);
    const matches = state.match(regexPattern);

    return matches ? matches[1] : '';
};

export const getSocialProviderFromState = (state: string) => {
    return getStateData(state, 'provider');
};

export const getSocialSignUpProviderSourceFromState = (state: string) => {
    return getStateData(state, 'source');
};

export const getInStoreDataFromStorage = () => {
    const state = BrowserStorage.getItem(StorageKey.SOCIAL_STATE, 'localStorage') || '';

    return { store: getStateData(state, 'store'), receiptId: getStateData(state, 'receiptId') };
};

export const getSocialProviderFromStorage = (): string => {
    const providerState = BrowserStorage.getItem(StorageKey.SOCIAL_STATE, 'localStorage');

    return providerState ? getSocialProviderFromState(providerState) : '';
};

export const getHideSocialPositionWrapper = (
    socialProviders: ISocialProvider[] = [],
    isSignInToConnect: boolean
): boolean => {
    // if user hasn't clicked social provider and social providers' length > 0, we show position wrapper, and if user clicked social provider and hasn't completed consent form, we show it if socialProvider.length > 0
    if (!isSignInToConnect) {
        return socialProviders.length === 0;
    }
    const providerName = getSocialProviderFromStorage();
    const filteredProviders = socialProviders?.filter((item) => item.providerName !== providerName);

    return filteredProviders.length === 0;
};

export const getCurrentSocialProvider = (socialProviders: ISocialProvider[] = []) => {
    const providerName = getSocialProviderFromStorage();

    return socialProviders.find((item) => item.providerName === providerName);
};

// get redirect url which only includes tenant
// abandon other search params from social call back:
// such as http://localhost:4010/sign-up?tenant=clinique.jp&code=GDXOhXJQ07VWMwwWEwHD&state=line%7Ct8r9h87in915jzh3ad0q)
export const getSocialRedirectUri = (): string => {
    const { origin, pathname } = window.location;
    const tenant = new URLSearchParams(window.location.search).get('tenant');

    return origin + pathname + (tenant ? `?tenant=${tenant}` : '');
};

export const getSocialSignUpProviderSource = () => {
    const provider = BrowserStorage.getItem(
        StorageKey.SOCIAL_SIGN_UP_PROVIDER_SOURCE,
        'sessionStorage'
    );
    if (!provider) {
        return undefined;
    }

    const type = (provider as unknown) as SocialSignUpType;
    const indexOfType = Object.values(SocialSignUpType).indexOf(type);
    if (indexOfType < 0) {
        return undefined;
    }

    return SocialSignUpType[Object.keys(SocialSignUpType)[indexOfType] as SocialSignUpTypeKeys];
};

export const setSocialSignUpProviderSourceToSessionStorage = (state: string) => {
    const socialSignUpProviderSource = getSocialSignUpProviderSourceFromState(state);
    socialSignUpProviderSource &&
        BrowserStorage.setItem(
            StorageKey.SOCIAL_SIGN_UP_PROVIDER_SOURCE,
            socialSignUpProviderSource,
            'sessionStorage'
        );
};

export const getSocialPageLoadType = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const queries = [
        SocialCallbackQueryParams.STATE,
        SOCIAL_QUERY_CONNECT,
        SOCIAL_QUERY_DISCONNECT
    ];

    const type = queries.find((query) => urlParams.has(query));

    return type && SocialPageLoadType[type.toUpperCase() as keyof typeof SocialPageLoadType];
};
